import React, {useContext} from 'react'
import {Alert, FormControlLabel, Radio, Stack, Typography} from '@mui/material'
import {Trans} from 'react-i18next'
import productTypeIsOneOf from '../../../lib/productTypeIsOneOf'
import {ComplaintState, ComplaintTypeOptions, ProductTypeOptions,} from '../../../contexts/types'
import {OrgCustomizationContext} from "../../../themes/OrgCustomizationProvider";
import MultiImageInput from "../../Inputs/MultiImageInput";

type LabelingPackagingOptionsProps = {
    productType: ProductTypeOptions
    selectedOption: ComplaintTypeOptions
    complaint: ComplaintState
    attachmentIds: number[]
    setAttachmentIds: React.Dispatch<number[]>,
    attachmentsError?: string,
}

const LabelingPackagingOptions: React.FC<LabelingPackagingOptionsProps> = ({
                                                                               productType,
                                                                               selectedOption,
                                                                               complaint,
                                                                               attachmentIds,
                                                                               setAttachmentIds,
                                                                               attachmentsError,
                                                                           }) => {
    const {t, tns, orgIdentifier} = useContext(OrgCustomizationContext)
    const eventOccurrence = complaint.complaintType.location

    return (
        <>
            {/* Label / Packaging */}
            <Typography
                variant='body1'
                color='secondary.contrastText'
                sx={{pt: 2}}
            >
                <Trans i18nKey={tns('cti_complaint_labeling_packaging')}>
                    -
                    <Typography component='span' sx={{color: 'primary.main'}}>
                        -
                    </Typography>
                    -
                </Trans>
            </Typography>

            <FormControlLabel
                value='FOREIGN_PARTICLES'
                control={<Radio/>}
                label={t('cti_option_foreign_particles') as string}
            />
            <FormControlLabel
                value='LABEL_ILLEGIBLE'
                control={<Radio/>}
                label={t('cti_option_label_illegible') as string}
            />
            <FormControlLabel
                value='LABEL_MISLEADING'
                control={<Radio/>}
                label={t('cti_option_label_misleading') as string}
            />
            <FormControlLabel
                value='LABEL_INFORMATION_MISSING'
                control={<Radio/>}
                label={t('cti_option_label_information_missing') as string}
            />
            <FormControlLabel
                value='WRONG_PART_PACKAGED'
                control={<Radio/>}
                label={t('cti_option_wrong_part_packaged') as string}
            />

            {
                orgIdentifier === 'Nobel' && selectedOption === 'WRONG_PART_PACKAGED' &&
                <Stack direction='column' spacing={1}>
                    {attachmentIds?.length === 0 && <Alert severity='info'>
                        {t('cti_please_include_pictures_of_packaging')}
                    </Alert>}

                    <MultiImageInput attachmentIds={attachmentIds}
                                     setAttachmentIds={setAttachmentIds}
                                     tag='productComplaint'
                                     errorText={attachmentsError}
                    />
                </Stack>
            }

            <FormControlLabel
                value='STERILE_BARRIER_DAMAGED'
                control={<Radio/>}
                label={t('cti_option_sterile_barrier_damaged') as string}
            />

            {selectedOption === 'STERILE_BARRIER_DAMAGED' && (
                <Alert severity='warning'>
                    {t('cti_not_applicable_warning')}
                </Alert>
            )}

            <FormControlLabel
                value='DAMAGE_WITH_PACKAGING_INTACT'
                control={<Radio/>}
                label={t('cti_option_damage_with_packaging_intact') as string}
            />

            {
                orgIdentifier === 'Nobel' && selectedOption === 'DAMAGE_WITH_PACKAGING_INTACT' &&
                <Stack direction='column' spacing={1}>
                    {attachmentIds?.length === 0 && <Alert severity='info'>
                        {t('cti_please_include_pictures_of_packaging')}
                    </Alert>}

                    <MultiImageInput attachmentIds={attachmentIds}
                                     setAttachmentIds={setAttachmentIds}
                                     tag='productComplaint'
                                     errorText={attachmentsError}
                    />
                </Stack>
            }

            <FormControlLabel
                value='PART_DISASSEMBLED'
                control={<Radio/>}
                label={t('cti_option_part_disassembled') as string}
            />

            {productTypeIsOneOf(
                    ['ABUTMENT', 'CLINICAL_INSTRUMENT', 'LAB_INSTRUMENT_LAB_SCREW'],
                    productType
                ) &&
                (eventOccurrence === '1_LAB_INITIAL_INSPECTION' ||
                    eventOccurrence === '2_LAB_DURING_FINALIZATION') && (
                    <FormControlLabel
                        value='INADEQUATE_CLEANING_OF_PART'
                        control={<Radio/>}
                        label={
                            t(
                                'cti_option_inadequate_cleaning_of_part'
                            ) as string
                        }
                    />
                )}
        </>
    )
}

export default LabelingPackagingOptions
